import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const loadLeaflet = () => new Promise((resolve, reject) => {
  if (window.L) { resolve(window.L); return; }
  if (!document.querySelector('link[href*="leaflet.css"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  const existing = document.querySelector('script[src*="leaflet.js"]');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.L));
    existing.addEventListener('error', reject);
    return;
  }
  const script = document.createElement('script');
  script.src = LEAFLET_JS;
  script.async = true;
  script.onload = () => resolve(window.L);
  script.onerror = () => reject(new Error('Falha ao carregar o mapa'));
  document.body.appendChild(script);
});

export default function MapaGps() {
  const navigate = useNavigate();
  const [graves, setGraves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [block, setBlock] = useState('');
  const [sector, setSector] = useState('');
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    api.get('/graves-with-gps')
      .then(({ data }) => { setGraves(data); setLoading(false); })
      .catch(() => { setError('Erro ao carregar os jazigos com GPS.'); setLoading(false); });
  }, []);

  const blocks = useMemo(() => [...new Set(graves.map((g) => g.block))].sort(), [graves]);
  const sectors = useMemo(
    () => [...new Set(graves.filter((g) => !block || g.block === block).map((g) => g.sector))].filter(Boolean).sort(),
    [graves, block]
  );

  const filtered = useMemo(
    () => graves.filter((g) => (!block || g.block === block) && (!sector || g.sector === sector)),
    [graves, block, sector]
  );

  useEffect(() => {
    if (!graves.length) return;
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled) return;
      if (!mapRef.current) {
        const first = filtered[0] || graves[0];
        const map = L.map('mapa-gps').setView([Number(first.latitude), Number(first.longitude)], 17);
        const satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          maxZoom: 21,
          maxNativeZoom: 18,
          attribution: 'Imagens &copy; Esri',
        });
        const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 21, maxNativeZoom: 19, attribution: '&copy; OpenStreetMap' });
        const labels = L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
          maxZoom: 21,
          maxNativeZoom: 18,
          pane: 'overlayPane',
        });
        satellite.addTo(map);
        L.control.layers({ 'Satélite': satellite, 'Mapa': osm }, { 'Rótulos de ruas': labels }, { position: 'topright' }).addTo(map);
        layerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;
        setTimeout(() => map.invalidateSize(), 200);
      }
      const L2 = window.L;
      const layer = layerRef.current;
      if (!layer) return;
      layer.clearLayers();
      filtered.forEach((g) => {
        const lat = Number(g.latitude);
        const lng = Number(g.longitude);
        const low = g.gps_accuracy_m != null && g.gps_accuracy_m > 10;
        const icon = L2.divIcon({
          className: '',
          html: `<div style="width:14px;height:14px;border-radius:50%;background:${low ? '#B85B1B' : '#188A43'};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        });
        L2.marker([lat, lng], { icon })
          .bindPopup(
            `<strong>Jazigo ${g.number}</strong><br/>Quadra ${g.block}${g.sector ? ` — Setor ${g.sector}` : ''}` +
            (g.gps_accuracy_m != null ? `<br/>Precisão: ${Math.round(g.gps_accuracy_m)} m` : '')
          )
          .addTo(layer);
      });
      if (filtered.length) {
        const bounds = L2.latLngBounds(filtered.map((g) => [Number(g.latitude), Number(g.longitude)]));
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
      }
    }).catch(() => setError('Não foi possível carregar o mapa. Verifique sua conexão.'));
    return () => { cancelled = true; };
  }, [filtered, graves]);

  return (
    <div className="results-page">
      <div className="container results-container">
        <span className="back-link" onClick={() => navigate('/')}>← Início</span>

        <div className="como-chegar-header" style={{ marginTop: 8 }}>
          <h2>Mapa dos jazigos com GPS</h2>
          <p>
            {loading ? 'Carregando…' : `${filtered.length} de ${graves.length} jazigos cadastrados com localização`}
          </p>
        </div>

        <div className="mapa-gps-filters">
          <select className="form-select" value={block} onChange={(e) => { setBlock(e.target.value); setSector(''); }}>
            <option value="">Todas as quadras</option>
            {blocks.map((b) => <option key={b} value={b}>Quadra {b}</option>)}
          </select>
          <select className="form-select" value={sector} onChange={(e) => setSector(e.target.value)} disabled={!block}>
            <option value="">{block ? 'Todos os setores' : 'Selecione a quadra'}</option>
            {sectors.map((s) => <option key={s} value={s}>Setor {s}</option>)}
          </select>
        </div>

        {error && <p className="gps-error">{error}</p>}

        <div id="mapa-gps" className="como-chegar-map" style={{ height: 480 }} />

        <p className="como-chegar-legal">
          Pontos verdes: precisão até 10 m. Pontos alaranjados: precisão acima de 10 m (confira a placa no local).
        </p>
      </div>
    </div>
  );
}
